import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Session, User, AuthError } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import { getDevSession } from '@/lib/dev-login'

interface AuthContextType {
  user: User | null
  session: Session | null
  loading: boolean
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signUp: (email: string, password: string) => Promise<{ data: { user: User | null; session: Session | null }; error: AuthError | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/** Build a synthetic User from a dev session for local development */
function buildDevUser(devSession: NonNullable<ReturnType<typeof getDevSession>>): User {
  return {
    id: devSession.user.id,
    email: devSession.user.email,
    app_metadata: {},
    user_metadata: { name: devSession.user.name, role: devSession.user.role },
    aud: 'authenticated',
    created_at: new Date().toISOString(),
    role: devSession.user.role,
  } as unknown as User
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false

    // Timeout to avoid hanging when Supabase is unreachable
    const timeout = setTimeout(() => {
      if (cancelled.current) return
      // On timeout, try dev session as fallback
      const devSession = getDevSession()
      if (devSession) {
        setUser(buildDevUser(devSession))
      }
      setLoading(false)
    }, 3000)

    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      clearTimeout(timeout)
      if (cancelled.current) return
      if (s) {
        setSession(s)
        setUser(s.user)
      } else {
        // No real session — check for dev session fallback
        const devSession = getDevSession()
        if (devSession) {
          setUser(buildDevUser(devSession))
        }
      }
      setLoading(false)
    }).catch(() => {
      clearTimeout(timeout)
      if (cancelled.current) return
      // On Supabase error, try dev session as fallback
      const devSession = getDevSession()
      if (devSession) {
        setUser(buildDevUser(devSession))
      }
      setLoading(false)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'TOKEN_REFRESHED' && s) {
        setSession(s)
      }
      if (s) {
        setSession(s)
        setUser(s.user)
      } else {
        setSession(null)
        // Don't overwrite a dev user with null — only clear if no dev session
        const devSession = getDevSession()
        if (!devSession) {
          setUser(null)
        }
      }
      setLoading(false)
    })

    return () => {
      cancelled.current = true
      clearTimeout(timeout)
      subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error }
  }

  const signUp = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    return { data: { user: data.user ?? null, session: data.session ?? null }, error }
  }

  const signOut = async () => {
    // Clear dev session if present
    localStorage.removeItem('phyre_dev_session')
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
